import React, { useState, useEffect } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";

const Dashboard = () => {
    const { master } = getAPI();

    const [search, setSearch] = useState("");
    const [records, setRecords] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    // ✅ Fetch Data (Single Function)
    const fetchRecords = async (pageNo = page, searchValue = search, perPage = limit) => {
        try {
            setLoading(true);
            const res = await axios.post(master.dashboardApi, {
                page: pageNo,
                limit: perPage,
                search: searchValue,
            });

            if (res.data.flag === 1 && res.data.code === 200) {
                const apiData = res.data.data;
                setRecords(apiData.data || []);
                setTotalPages(apiData.pagination?.totalPages || 1);
                setPage(apiData.pagination?.currentPage || pageNo);
            } else {
                setRecords([]);
                setTotalPages(1);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            setRecords([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Load Once (Initial)
    useEffect(() => {
        fetchRecords(1, "", limit);
        // eslint-disable-next-line
    }, []);

    // ✅ Search Click
    const handleSearch = () => {
        setPage(1);
        fetchRecords(1, search, limit);
    };

    // ✅ Page Change
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            fetchRecords(newPage, search, limit);
        }
    };

    // ✅ Limit Change (Single Call)
    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setLimit(newLimit);
        setPage(1);
        fetchRecords(1, search, newLimit);
    };

    return (
        <main id="main" className="main">
            {/* Page Title */}
            <div className="pagetitle mb-4">
                <h1>Centralized Data</h1>
                <nav>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <a href="/">Home</a>
                        </li>
                        <li className="breadcrumb-item active">Centralized Data</li>
                    </ol>
                </nav>
            </div>

            {/* Dashboard Section */}
            <section className="section dashboard">
                <div className="card shadow-sm">
                    <div className="card-body py-4">
                        {/* Search Section */}
                        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                            <div className="input-group" style={{ maxWidth: "350px" }}>
                                <input
                                    type="text"
                                    className="form-control me-2 mb-2 mb-sm-0"
                                    placeholder="Enter contact number"
                                    value={search}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSearch(value);
                                        if (value === "") {
                                            // If search box is cleared, reset page and fetch all data
                                            setPage(1);
                                            fetchRecords(1, "");
                                        }
                                    }}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSearch}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Searching...
                                        </>
                                    ) : (
                                        "Search"
                                    )}
                                </button>
                            </div>


                            {/* Rows per page */}
                            <div className="d-flex align-items-center">
                                <label className="me-2 text-muted">Rows per page:</label>
                                <select
                                    className="form-select"
                                    style={{ width: "80px" }}
                                    value={limit}
                                    onChange={handleLimitChange}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>

                        {/* Table Section */}
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-secondary">Fetching records...</p>
                            </div>
                        ) : records.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>No.</th>
                                            <th>Order Code</th>
                                            <th>Customer Name</th>
                                            <th>Phone</th>
                                            <th>Payment Method</th>
                                            <th>Products</th>
                                            <th>Courier</th>
                                            <th>Status</th>
                                            <th>Total Amount</th>
                                            <th>Order Date</th>
                                            <th>Source</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.map((item, index) => (
                                            <tr key={item.order_code}>
                                                <td>{(page - 1) * limit + index + 1}</td>
                                                <td>{item.order_code}</td>
                                                <td>{item.customer_name || "-"}</td>
                                                <td>{item.phone_number || "-"}</td>
                                                <td>{item.payment_method || "-"}</td>
                                                <td style={{ whiteSpace: "pre-wrap" }}>
                                                    {item.products || "-"}
                                                </td>
                                                <td>{item.shipping_courier || "-"}</td>
                                                <td>
                                                    <span
                                                        className={`badge ${item.status === "COMPLETE"
                                                            ? "bg-success"
                                                            : item.status === "CANCELLED"
                                                                ? "bg-danger"
                                                                : "bg-warning text-dark"
                                                            }`}
                                                    >
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td>₹{item.total_amount || "-"}</td>
                                                <td>{item.order_date || "-"}</td>
                                                <td>{item.source || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-center text-muted py-4 mb-0">No records found</p>
                        )}

                        {/* Pagination */}
                        {records.length > 0 && (
                            <nav className="mt-4">
                                <ul className="pagination justify-content-center mb-0">
                                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handlePageChange(page - 1)}
                                        >
                                            Previous
                                        </button>
                                    </li>

                                    {[...Array(totalPages)].map((_, i) => {
                                        if (
                                            i + 1 === 1 ||
                                            i + 1 === totalPages ||
                                            (i + 1 >= page - 1 && i + 1 <= page + 1)
                                        ) {
                                            return (
                                                <li
                                                    key={i}
                                                    className={`page-item ${page === i + 1 ? "active" : ""
                                                        }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(i + 1)}
                                                    >
                                                        {i + 1}
                                                    </button>
                                                </li>
                                            );
                                        } else if (
                                            i + 1 === page - 2 ||
                                            i + 1 === page + 2
                                        ) {
                                            return (
                                                <li key={i} className="page-item disabled">
                                                    <span className="page-link">...</span>
                                                </li>
                                            );
                                        } else return null;
                                    })}

                                    <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handlePageChange(page + 1)}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Dashboard;
